import { Fragment } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    CollapseContentSection,
    Divider,
    useBelowBreakpoint,
} from "@impero-com/ui";
import { useQueryClient } from "@tanstack/react-query";
import { Switch } from "antd";
import { cx } from "class-variance-authority";

import { UpdateNotificationSettingsValues } from "api/generated/model";

import {
    USER_WITH_DETAILS_QUERY_KEY,
    useUserWithDetailsQuery,
} from "../user/queries/useUserWithDetailsQuery";

import * as theme from "./NotificationPreferences.css";
import { useUpdateUserMutation } from "./queries/useUpdateUserMutation";

type NotificationSettingKey = keyof UpdateNotificationSettingsValues;

interface NotificationPreferencesProps {
    userId: number;
}

export function NotificationPreferences({
    userId,
}: NotificationPreferencesProps) {
    const { t } = useTranslation();
    const belowLg = useBelowBreakpoint("lg");
    const queryClient = useQueryClient();
    const { data: userWithDetails } = useUserWithDetailsQuery(userId);
    const updateUser = useUpdateUserMutation();
    const preferences = userWithDetails.linkedResources.notificationSettings;

    // Patch each setting on its own change, like the email-grouping select.
    const handleToggle = (
        settingKey: keyof UpdateNotificationSettingsValues,
        enabled: boolean
    ) => {
        updateUser.mutate({
            userId,
            data: {
                notificationSettings: {
                    [settingKey]: enabled,
                },
            },
        });
        queryClient.invalidateQueries({
            queryKey: [USER_WITH_DETAILS_QUERY_KEY, userId],
        });
    };

    const groupRows: PreferenceGroupData[][] = [
        [
            {
                title: t(
                    "MyProfile_NotificationPreferences_WorkAssigned_Title"
                ),
                description: t(
                    "MyProfile_NotificationPreferences_WorkAssigned_Description"
                ),
                toggles: [
                    {
                        settingKey: "assignmentNotifications",
                        label: t(
                            "MyProfile_NotificationPreferences_WorkAssigned_Assignments"
                        ),
                        enabled: preferences.assignmentNotifications,
                    },
                    {
                        settingKey: "deadlineReminders",
                        label: t(
                            "MyProfile_NotificationPreferences_WorkAssigned_Deadlines"
                        ),
                        enabled: preferences.deadlineReminders,
                    },
                ],
            },
            {
                title: t("MyProfile_NotificationPreferences_Reviews_Title"),
                description: t(
                    "MyProfile_NotificationPreferences_Reviews_Description"
                ),
                toggles: [
                    {
                        settingKey: "reviewRequests",
                        label: t(
                            "MyProfile_NotificationPreferences_Reviews_Requests"
                        ),
                        enabled: preferences.reviewRequests,
                    },
                    {
                        settingKey: "reviewReminders",
                        label: t(
                            "MyProfile_NotificationPreferences_Reviews_Reminders"
                        ),
                        enabled: preferences.reviewReminders,
                    },
                ],
            },
            {
                title: t("MyProfile_NotificationPreferences_Reports_Title"),
                description: t(
                    "MyProfile_NotificationPreferences_Reports_Description"
                ),
                toggles: [
                    {
                        settingKey: "reportDeliveries",
                        label: t(
                            "MyProfile_NotificationPreferences_Reports_Deliveries"
                        ),
                        enabled: preferences.reportDeliveries,
                    },
                ],
            },
        ],
        [
            {
                title: t(
                    "MyProfile_NotificationPreferences_ControlTesting_Title"
                ),
                description: t(
                    "MyProfile_NotificationPreferences_ControlTesting_Description"
                ),
                toggles: [
                    {
                        settingKey: "testingAssignmentsReminders",
                        label: t(
                            "MyProfile_NotificationPreferences_ControlTesting_Assignments"
                        ),
                        enabled: preferences.testingAssignmentsReminders,
                    },
                ],
            },
            {
                title: t("MyProfile_NotificationPreferences_Approvals_Title"),
                description: t(
                    "MyProfile_NotificationPreferences_Approvals_Description"
                ),
                toggles: [
                    {
                        settingKey: "approvalConfirmations",
                        label: t(
                            "MyProfile_NotificationPreferences_Approvals_Confirmations"
                        ),
                        enabled: preferences.approvalConfirmations,
                    },
                    {
                        settingKey: "watchedControlCompletions",
                        label: t(
                            "MyProfile_NotificationPreferences_Approvals_Completions"
                        ),
                        enabled: preferences.watchedControlCompletions,
                    },
                ],
            },
        ],
    ];

    const alwaysSent = [
        t("MyProfile_NotificationPreferences_AlwaysSent_Rejections"),
        t("MyProfile_NotificationPreferences_AlwaysSent_ManualReminders"),
        t("MyProfile_NotificationPreferences_AlwaysSent_Account"),
    ];

    return (
        <CollapseContentSection
            titleLabel={t("MyProfile_NotificationPreferences_Title")}
            subtitleLabel={t("MyProfile_NotificationPreferences_Description")}
            level={4}
            styleVariant="primary"
        >
            <div className={theme.preferences}>
                {groupRows.map((row, index) => (
                    <Fragment key={row[0].title}>
                        {index > 0 && <Divider />}
                        <div className={theme.groupRow}>
                            {row.map((group) => (
                                <PreferenceGroup
                                    key={group.title}
                                    group={group}
                                    stacked={belowLg}
                                    onToggle={handleToggle}
                                />
                            ))}
                        </div>
                    </Fragment>
                ))}

                <Divider />

                <div className={theme.group}>
                    <h4 className={theme.groupTitle}>
                        {t(
                            "MyProfile_NotificationPreferences_AlwaysSent_Title"
                        )}
                    </h4>
                    <p className={theme.groupDescription}>
                        {t(
                            "MyProfile_NotificationPreferences_AlwaysSent_Description"
                        )}
                    </p>
                    <ul className={theme.alwaysSentList}>
                        {alwaysSent.map((item) => (
                            <li key={item}>{item}</li>
                        ))}
                    </ul>
                </div>
            </div>
        </CollapseContentSection>
    );
}

interface PreferenceToggle {
    settingKey: NotificationSettingKey;
    label: string;
    enabled: boolean;
}

interface PreferenceGroupData {
    title: string;
    description: string;
    toggles: PreferenceToggle[];
}

interface PreferenceGroupProps {
    group: PreferenceGroupData;
    stacked: boolean;
    onToggle: (settingKey: NotificationSettingKey, enabled: boolean) => void;
}

function PreferenceGroup({ group, stacked, onToggle }: PreferenceGroupProps) {
    return (
        <div
            className={cx(
                theme.group,
                stacked ? theme.groupStacked : theme.groupColumn
            )}
        >
            <h4 className={theme.groupTitle}>{group.title}</h4>
            <p className={theme.groupDescription}>{group.description}</p>
            {group.toggles.map((toggle) => (
                <div key={toggle.label} className={theme.toggleRow}>
                    <Switch
                        checked={toggle.enabled}
                        onChange={(value) => onToggle(toggle.settingKey, value)}
                    />
                    <span>{toggle.label}</span>
                </div>
            ))}
        </div>
    );
}
